import Vue from 'vue'
import axios from 'axios'
import Router, { Route } from 'vue-router'

export interface State {}

export type Response = {
  data: object
  headers: {}
}

export type Error = {
  response: {
    status: number
    data: {
      user_msg: string
    }
  }
}

export type ResObject = {
  response?: Response | undefined
  error?: Error | undefined
}

export class Api {
  api: any
  router: Router
  store: any

  constructor(router: Router, store: any) {
    this.api = axios.create({
      // baseURL: process.env.VUE_APP_API_BASE_URI,
      baseURL: 'https://ichiban.themint.jp',
      timeout: 10000,
      withCredentials: true,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
        // 'X-Requested-With': 'XMLHttpRequest'
      },
      responseType: 'json'
    })
    this.router = router
    this.store = store
  }

  async get(endopoint: string, params?: object) {
    const response: Response = await this.api.get(endopoint, params)
    return { response: response.data }
  }

  async post(endopoint: string, data?: object) {
    const response: Response = await this.api.post(endopoint, data)
    return { response: response.data }
  }

  handleError(error: Error) {
    if (error.response && error.response.status === 500) {
      this.store.dispatch('flash/setMessage', {
        message: '予期せぬエラーが発生しました',
        isAlert: true
      })
      return
    }
    if (error.response && error.response.status === 401) {
      this.store.dispatch('flash/setMessage', {
        message: 'ログインが必要です',
        isAlert: true
      })
      return
    }
    if (error.response && error.response.data) {
      this.store.dispatch('flash/setMessage', {
        message: error.response.data.user_msg,
        isAlert: true
      })
    } else {
      this.store.dispatch('flash/setMessage', {
        message: '予期せぬエラーが発生しました',
        isAlert: true
      })
    }
  }

  /**
   * 1-2 ログイン - [ログイン]ボタン
   * @param data - POSTデータ
   */
  async postSignIn(data = {}) {
    const response: Response['data'] = await this.post('/api/users/signin', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 1-3 パスワードリセット - [送信]ボタン
   * @param data - POSTデータ
   */
  async postResetPassword(data = {}) {
    const response: Response['data'] = await this.post('/api/users/send_reset_password_mail', withUI(data)).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 1-4 会員登録 - [会員登録]ボタン
   * @param data - POSTデータ
   */
  async postSignUp(data = {}) {
    const response: Response['data'] = await this.post('/api/users/signup', withUI(data)).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })

    return response
  }

  /**
   * 2-1 ログイン後Top - 初期表示
   * @param params - リクエストパラメーター
   */
  async fetchHome(params = {}) {
    const response: Response['data'] = await this.get('/api/home', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 2-1 ログイン後Top - お知らせをもっと見る
   * @param params - リクエストパラメーター
   */
  async fetchNotices(
    params:
      | {
          since_id: number | undefined
          limit: number
        }
      | {} = {}
  ) {
    const response: Response['data'] = await this.get('/api/notices', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 2-1 ログイン後Top - お知らせをもっと見る
   * @param params - リクエストパラメーター
   */
  async fetchBenefits(
    params:
      | {
          since_id: number | undefined
          limit: number
        }
      | {} = {}
  ) {
    const response: Response['data'] = await this.get('/api/benefits', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 3-1 特典詳細(交換用) - [交換する]ボタン
   * @param data - POSTデータ
   */
  async postTickets(data = {}) {
    const response: Response['data'] = await this.post('/api/tickets/issue', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 4-1 保有特典の一覧 - 初期表示
   * @param params - リクエストパラメーター
   */
  async fetchTickets(params = {}) {
    const response: Response['data'] = await this.get('/api/tickets', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 4-2 特典詳細 - 初期表示
   * @param params - リクエストパラメーター
   */
  async fetchBenefitsDetail(params: { benefit_id: number }) {
    const response: Response['data'] = await this.get(`/api/benefits/${params.benefit_id}`, {
      params: {}
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 4-3 特典行使(対面/非対面) - 詳細から[使用する]ボタン
   * @param data - POSTデータ
   */
  async postTicketsUse(data: { ticket_id: string; email?: string; message?: string; latitude?: number; longitude?: number }) {
    const formData: () => {
      email: string
      message: string
      latitude?: number
      longitude?: number
    } = () => {
      // TODO: 暫定対応、emailとmessageが空の場合も受け付けるようにして修正
      const v = {
        email: data.email ? data.email : 'develop@themint.jp',
        message: data.message ? data.message : ''
      }
      if (data.longitude && data.latitude) {
        return {
          ...v,
          latitude: data.latitude,
          longitude: data.longitude
        }
      }
      return v
    }
    const response: Response['data'] = await this.post(`/api/tickets/${data.ticket_id}/use`, { ...formData() }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 5-1 ポイント送受信履歴 - 初期表
   * @param params - リクエストパラメーター
   */
  async fetchTransactions(params: { since_id: number; limit: number }) {
    const response: Response['data'] = await this.get('/api/transactions', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 6-1 チェックイン候補一覧 > 初期表示
   * @param params - リクエストパラメーター
   */
  async fetchCheckInStamps(params: { latitude: number; longitude: number }) {
    const response: Response['data'] = await this.get(`/api/stamps`, {
      params: {
        latitude: params.latitude,
        longitude: params.longitude,
        order_by: 'distance',
        limit: 100
      }
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * ポイント受け取り（スタンプ情報取得）
   * @param params - リクエストパラメーター
   */
  async fetchStamp(params: { stamp_id: number }) {
    const response: Response['data'] = await this.get(`/api/stamps/${params.stamp_id}`).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * ポイント受け取り（ポイント獲得）
   * @param data - POSTデータ
   */
  async postCaptureStamp(data: { stamp_id: number; location?: { latitude: number; longitude: number } }) {
    const response: Response['data'] = await this.post(`/api/stamps/${data.stamp_id}/capture`, data.location).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 6-1 チェックイン候補一覧 - [チェックイン]ボタン
   * @param params - リクエストパラメーター
   */
  async postCheckIn(data: { stamp_id: number; location: { latitude: number; longitude: number } }) {
    const response: Response['data'] = await this.post(`/api/stamps/${data.stamp_id}/capture`, data.location).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-1 設定一覧 - 初期表示
   * @param params - リクエストパラメーター
   */
  async fetchUsersMe(params = {}) {
    const response: Response['data'] = await this.get('/api/users/me', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-2 プロフィール設定 - 設定操作
   * @param data - POSTデータ
   */
  async postUpdateProfile(data: { name: string; screen_name: string }) {
    const response: Response['data'] = await this.post('/api/users/update_profile', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-3 アドレス変更 - 設定操作
   * @param data - POSTデータ
   */
  async postUpdateEmail(data: { email: string }) {
    const response: Response['data'] = await this.post('/api/users/update_email', withUI(data)).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-4 パスワード変更 - 設定操作
   * @param data - POSTデータ
   */
  async postUpdatePassword(data: { current_password: string | undefined; new_password: string }) {
    const response: Response['data'] = await this.post('/api/users/update_password', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-5 Twitter連携 - url取得
   */
  async fetchTwitterUrl(params = {}) {
    const response: Response['data'] = await this.get('/api/users/twitter_oauth_url', {
      params
    }).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-5 Twitter連携 - 連携操作
   * @param data - POSTデータ
   */
  async postConnectTwitter(data: { access_token: string }) {
    const response: Response['data'] = await this.post('/api/users/connect_twitter', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }

  /**
   * 7-5 Twitter連携 - 解除操作
   * @param data - POSTデータ
   */
  async postDisconnectTwitter(data = {}) {
    const response: Response['data'] = await this.post('/api/users/disconnect_twitter', data).catch((error: Error) => {
      this.handleError(error)
      return { error }
    })
    return response
  }
}

const install = (Vue: any, options: any) => {
  const API = new Api(options.router, options.store)

  options.router.beforeEach((to: Route, from: Route, next: any) => {
    if (to.matched.some(record => record.meta.isPublic)) {
      next()
    } else {
      // next({ path: '/' })
      next()
    }
  })

  // Vueコンポーネントへ注入
  Object.defineProperty(Vue.prototype, '$api', {
    get(this: Vue) {
      return API
    }
  })
  // Vuexストアへ注入
  Object.defineProperty(options.store, '$api', {
    get(this: any) {
      return API
    }
  })
}

export default {
  install
}

const withUI = (data: object): object => ({ ui: 'urawareds', ...data })
